import { ArrowGo, Pack, Pin, Truck, User } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { CheckCircle2, Clock, XCircle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/app-layout"
import { businessOrdersPath } from "@/routes"

interface Product {
  id: number
  name?: string
}

interface OrderItem {
  id: number
  quantity: number
  unit_price: number
  total_price: number
  notes?: string
  product: Product
}

interface User {
  id: number
  name: string
  email?: string
  avatar_url?: string
}

interface City {
  id: number
  name: string
}

interface Neighborhood {
  id: number
  name: string
  city?: City
}

interface Order {
  id: number
  order_number: string
  status: string
  delivery_type: string
  delivery_address?: {
    line1?: string
    details?: string
    street?: string
    floor?: string
    notes?: string
  }
  delivery_date?: string
  subtotal: number
  delivery_fee: number
  total: number
  payment_method: string
  payment_status: string
  notes?: string
  created_at: string
  user: User
  delivery_neighborhood?: Neighborhood
  order_items: OrderItem[]
}

interface Business {
  id: number
  business_name: string
}

interface StatusTransition {
  value: string
  label: string
}

interface Props {
  order: Order
  business: Business
  available_transitions: StatusTransition[]
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
  }).format(n)

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const shortOrderRef = (orderNumber: string) => {
  const parts = orderNumber.split("-")
  const last = parts[parts.length - 1] ?? orderNumber
  const short = last.slice(-6)
  return short.toUpperCase()
}

const statusLabel: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  preparing: "Preparando",
  ready: "Listo",
  delivering: "En camino",
  completed: "Completado",
  cancelled: "Cancelado",
}

const paymentMethodLabel: Record<string, string> = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
}

export default function BusinessOrderShow({
  order,
  business,
  available_transitions,
}: Props) {
  const handleStatusChange = (newStatus: string) => {
    router.patch(
      `/mi-negocio/pedidos/${order.id}`,
      { status: newStatus },
      { preserveScroll: true },
    )
  }

  const isDelivery = order.delivery_type === "delivery"
  const addressLine1 =
    order.delivery_address?.line1 ?? order.delivery_address?.street
  const addressDetails = order.delivery_address?.details
  const addressFloor = order.delivery_address?.floor
  const hasAddress = Boolean(addressLine1 ?? addressDetails ?? addressFloor)
  const deliveryNeighborhoodLabel = order.delivery_neighborhood?.city?.name
    ? `${order.delivery_neighborhood.name}, ${order.delivery_neighborhood.city.name}`
    : order.delivery_neighborhood?.name

  return (
    <AppLayout>
      <Head
        title={`Pedido #${shortOrderRef(order.order_number)} - ${business.business_name}`}
      />

      <div className="mx-auto max-w-3xl py-4">
        {/* Header */}
        <Link
          href={businessOrdersPath()}
          className="text-primary flex flex-wrap items-center gap-2 px-4 text-[13px] font-medium tracking-[0.13px]"
        >
          <div className="flex items-center gap-1">
            <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
            Pedidos
          </div>
        </Link>
        <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
          <Pack className="fill-primary h-8 w-8" />
          Pedido #{shortOrderRef(order.order_number)}
          <Badge>{statusLabel[order.status] ?? order.status}</Badge>
        </CardTitle>
        <CardContent className="text-muted-foreground p-4 text-sm">
          <div className="text-muted-foreground truncate font-mono text-xs">
            {order.order_number}
          </div>
          Pedido realizado el {formatDate(order.created_at)}
          {available_transitions.length > 0 && (
            <div className="mt-2 flex w-full flex-wrap items-center gap-2">
              <span className="text-muted-foreground self-center text-sm">
                Cambiar estado:
              </span>
              {available_transitions.map((transition) => (
                <Button
                  key={transition.value}
                  variant={
                    transition.value === "cancelled" ? "destructive" : "default"
                  }
                  size="sm"
                  onClick={() => handleStatusChange(transition.value)}
                >
                  {transition.value === "confirmed" && (
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                  )}
                  {transition.value === "preparing" && (
                    <Clock className="mr-1 h-4 w-4" />
                  )}
                  {transition.value === "ready" && (
                    <Pack className="mr-1 h-4 w-4" />
                  )}
                  {transition.value === "delivering" && (
                    <Truck className="mr-1 h-4 w-4" />
                  )}
                  {transition.value === "completed" && (
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                  )}
                  {transition.value === "cancelled" && (
                    <XCircle className="mr-1 h-4 w-4" />
                  )}
                  {transition.label}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
        {/* Status Actions */}

        {/* Customer Info */}
        <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-4 w-4" />
            Cliente: {order.user.name}
          </CardTitle>
          <CardContent className="space-y-2">
            {order.user.email && (
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <a
                  href={`mailto:${order.user.email}`}
                  className="hover:underline"
                >
                  {order.user.email}
                </a>
              </div>
            )}
          </CardContent>
        </div>

        {/* Delivery Info */}
        <div className="border-chart-4 w-full border-t-[1px] px-4 py-[20px]">
          <CardTitle className="flex items-center gap-2 text-base">
            {isDelivery ? (
              <Truck className="h-4 w-4" />
            ) : (
              <Pin className="h-4 w-4" />
            )}
            {isDelivery ? "Envio" : "Retiro en local"}
          </CardTitle>
          <CardContent className="space-y-3 text-sm">
            {isDelivery && deliveryNeighborhoodLabel && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Barrio</span>
                <span className="text-foreground text-right font-medium">
                  {deliveryNeighborhoodLabel}
                </span>
              </div>
            )}

            {isDelivery && hasAddress && (
              <div className="space-y-2">
                {addressLine1 && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Dirección</span>
                    <span className="text-foreground text-right font-medium">
                      {addressLine1}
                    </span>
                  </div>
                )}
                {addressFloor && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Piso</span>
                    <span className="text-foreground text-right font-medium">
                      {addressFloor}
                    </span>
                  </div>
                )}
                {addressDetails && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-muted-foreground">Detalles</span>
                    <span className="text-foreground text-right font-medium">
                      {addressDetails}
                    </span>
                  </div>
                )}
              </div>
            )}

            {order.notes && (
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground">Notas</span>
                <span className="text-foreground text-right font-medium">
                  {order.notes}
                </span>
              </div>
            )}
          </CardContent>
          <div className="pt-3 pr-6 text-sm">
            {order.payment_method && (
              <div className="flex justify-between">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Metodo de pago
                </CardTitle>
                <span className="font-medium">
                  {paymentMethodLabel[order.payment_method] ??
                    order.payment_method}
                </span>
              </div>
            )}
            {order.payment_status && (
              <div className="justify-betweens flex">
                <CardTitle className="text-chart-5 pb-[5px] text-[14px] leading-[21px] font-semibold tracking-[0.14px]">
                  Estado del pago
                </CardTitle>
                <Badge
                  variant={
                    order.payment_status === "paid" ? "default" : "outline"
                  }
                >
                  {order.payment_status === "pending"
                    ? "Pendiente"
                    : order.payment_status === "paid"
                      ? "Pagado"
                      : "Reembolsado"}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-card w-full px-4 py-[20px]">
          <CardTitle className="flex items-center gap-2 text-base">
            <Pack className="h-4 w-4" />
            Productos ({order.order_items.length})
          </CardTitle>
          <CardContent className="space-y-3">
            {order.order_items.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    {item.product.name ?? "Producto"}{" "}
                    <span className="text-muted-foreground">
                      x{item.quantity}
                    </span>
                  </div>
                  {item.notes && (
                    <div className="text-muted-foreground text-sm">
                      Nota: {item.notes}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-medium">
                    {formatPrice(item.total_price)}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {formatPrice(item.unit_price)} c/u
                  </div>
                </div>
              </div>
            ))}

            <Separator className="my-3" />

            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium">
                  {formatPrice(order.subtotal)}
                </span>
              </div>
              {order.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <span>Envio</span>
                  <span className="font-medium">
                    {formatPrice(order.delivery_fee)}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-1 text-base">
                <span className="font-semibold">Total</span>
                <span className="font-bold">{formatPrice(order.total)}</span>
              </div>
            </div>
          </CardContent>
        </div>
      </div>
    </AppLayout>
  )
}
